// frontend/src/components/Dashboard/SolicitudesDemo.js
// Panel admin > Solicitudes demo: lo que llega del formulario público
// /solicitar-demo (backend/src/routes/solicitudesDemo.js). El admin las
// marca como contactadas o descartadas.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Inbox, Mail, Phone, Building2, MessageSquare, CheckCircle2, XCircle, RotateCcw } from 'lucide-react';
import './SolicitudesDemo.css';

const FILTROS = [
  { clave: '', texto: 'Todas' },
  { clave: 'nueva', texto: 'Nuevas' },
  { clave: 'contactada', texto: 'Contactadas' },
  { clave: 'descartada', texto: 'Descartadas' }
];

const ETIQUETA_ESTADO = { nueva: 'Nueva', contactada: 'Contactada', descartada: 'Descartada' };

const SolicitudesDemo = ({ showModal, onCambio }) => {
  const [filtro, setFiltro] = useState('nueva');
  const [solicitudes, setSolicitudes] = useState([]);
  const [conteos, setConteos] = useState({ nueva: 0, contactada: 0, descartada: 0 });
  const [cargando, setCargando] = useState(true);
  const [actualizando, setActualizando] = useState(null);
  // showModal cambia en cada render del Dashboard
  const showModalRef = useRef(showModal);
  showModalRef.current = showModal;

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const response = await fetch(`/api/solicitudes-demo${filtro ? `?estado=${filtro}` : ''}`, { credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `No se pudieron cargar las solicitudes (error ${response.status})`);
      setSolicitudes(data.solicitudes);
      setConteos(data.conteos);
    } catch (error) {
      showModalRef.current({ type: 'error', title: 'Error al cargar solicitudes', message: error.message });
    } finally {
      setCargando(false);
    }
  }, [filtro]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarEstado = async (solicitud, estado) => {
    setActualizando(solicitud.id);
    try {
      const response = await fetch(`/api/solicitudes-demo/${solicitud.id}/estado`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo actualizar la solicitud');
      await cargar();
      onCambio?.();
    } catch (error) {
      showModalRef.current({ type: 'error', title: 'Error', message: error.message });
    } finally {
      setActualizando(null);
    }
  };

  const total = conteos.nueva + conteos.contactada + conteos.descartada;

  return (
    <div className="dashboard-shell">
      <div className="dash-page-card">
        <div className="stack-manager-header">
          <h2><Inbox size={18} /> Solicitudes de demostración</h2>
        </div>
        <p className="text-muted solicitudes-sub">
          Personas que llenaron el formulario "Solicitar demostración" del landing.
        </p>

        <div className="solicitudes-filtros" role="tablist">
          {FILTROS.map(f => (
            <button
              key={f.clave || 'todas'}
              type="button"
              role="tab"
              aria-selected={filtro === f.clave}
              className={filtro === f.clave ? 'activo' : ''}
              onClick={() => setFiltro(f.clave)}
            >
              {f.texto} <span>{f.clave ? conteos[f.clave] : total}</span>
            </button>
          ))}
        </div>

        {cargando ? (
          <p className="text-muted">Cargando...</p>
        ) : solicitudes.length === 0 ? (
          <p className="text-muted">
            {filtro === 'nueva' ? 'No hay solicitudes nuevas. 🎉' : 'No hay solicitudes en esta categoría.'}
          </p>
        ) : (
          <div className="solicitudes-lista">
            {solicitudes.map(s => (
              <div key={s.id} className={`solicitud-item estado-${s.estado}`}>
                <div className="solicitud-cabecera">
                  <div>
                    <strong>{s.nombre}</strong>
                    <span className="solicitud-empresa"><Building2 size={13} /> {s.empresa}</span>
                  </div>
                  <span className={`solicitud-estado ${s.estado}`}>{ETIQUETA_ESTADO[s.estado] || s.estado}</span>
                </div>

                <div className="solicitud-contacto">
                  <a href={`mailto:${s.email}`}><Mail size={13} /> {s.email}</a>
                  <a href={`tel:${s.telefono.replace(/[^\d+]/g, '')}`}>
                    <Phone size={13} /> {s.telefono}{s.extension ? ` ext. ${s.extension}` : ''}
                  </a>
                </div>

                {s.comentarios && (
                  <p className="solicitud-comentarios"><MessageSquare size={13} /> {s.comentarios}</p>
                )}

                <div className="solicitud-pie">
                  <small className="text-muted">
                    Recibida el {new Date(s.created_at).toLocaleString('es-MX')}
                  </small>
                  <div className="solicitud-acciones">
                    {s.estado !== 'contactada' && (
                      <button type="button" className="btn-success" disabled={actualizando === s.id} onClick={() => cambiarEstado(s, 'contactada')}>
                        <CheckCircle2 size={13} /> Marcar contactada
                      </button>
                    )}
                    {s.estado !== 'descartada' && (
                      <button type="button" className="btn-secondary" disabled={actualizando === s.id} onClick={() => cambiarEstado(s, 'descartada')}>
                        <XCircle size={13} /> Descartar
                      </button>
                    )}
                    {s.estado !== 'nueva' && (
                      <button type="button" className="btn-secondary" disabled={actualizando === s.id} onClick={() => cambiarEstado(s, 'nueva')}>
                        <RotateCcw size={13} /> Volver a nueva
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SolicitudesDemo;
